import { textColorFields } from '../_shared/textColor.js';
import { mediaRadiusField, universalFields } from '../_shared/universal.js';
/**
 * feature-gallery — la « liste de points forts à galerie » des marques techniques (référence :
 * la page produit de nordarun.com, capturée par la fondatrice le 14/09) : à gauche UNE grande image
 * avec sa bande de vignettes, à droite la LISTE des points forts, empilée en gros titres — celui
 * qu'on lit est net, les autres sont en retrait. Cliquer un titre change l'image ET la phrase du bas.
 *
 * UNE PHOTO PAR POINT FORT, ET LA BANDE LES MONTRE TOUTES (fondatrice, 14/09). C'est le compte de la
 * référence : cinq lignes, cinq vignettes. La bande ne change donc JAMAIS — elle est la table des
 * matières de la section, et la vignette allumée dit où on en est. Deux versions écartées en chemin :
 * une galerie par point fort (la bande changeait sous le doigt) puis cinq emplacements par point fort
 * (la bande ne correspondait plus à la liste des titres).
 *
 * Vignette et titre sont donc deux entrées sur la MÊME chose — d'où un seul bouton radio par point
 * fort, sur lequel tombent les deux gestes.
 *
 * REMPLIR AVANT DE RÉGLER. Le bloc `feature` ne demande que trois choses — un titre, une phrase, une
 * photo — et la section trois réglages de mise en page. Rien d'autre : c'est une section qu'on remplit,
 * pas un tableau de bord.
 *
 * SANS JAVASCRIPT. UN bouton radio masqué par point fort, + `:checked ~` : le titre comme la vignette
 * ne sont que des `<label>` pointant dessus. Même moteur que `tabbed-content` — la section est
 * entièrement utilisable en SSR, script désactivé, et le premier point fort s'affiche par défaut.
 */
const CONTENT = 'Content';
const LAYOUT = 'Layout';
export const featureGallerySchema = {
    type: 'feature-gallery',
    name: 'Feature gallery',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Content ─────────────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: CONTENT,
            default: '',
            aiWritable: true,
            maxLength: 80,
            info: 'Optional — the list of features often speaks for itself.',
        },
        // ── Layout — trois réglages, pas un de plus ─────────────────────────────
        {
            id: 'mediaPosition',
            type: 'select',
            label: 'Media position',
            group: LAYOUT,
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: LAYOUT,
            default: 'square',
            options: [
                { value: 'square', label: 'Square' },
                { value: 'portrait', label: 'Portrait' },
                { value: 'landscape', label: 'Landscape' },
            ],
        },
        {
            id: 'showThumbnails',
            type: 'checkbox',
            label: 'Show thumbnails',
            group: LAYOUT,
            default: true,
            info: 'The strip under the big image holds one thumbnail per feature. Clicking one brings its photo forward and opens that feature.',
        },
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    blocks: [
        {
            type: 'feature',
            name: 'Feature',
            max: 8,
            fields: [
                {
                    id: 'title',
                    type: 'text',
                    label: 'Title',
                    default: 'Nouvel avantage',
                    defaultKey: 'section.new_benefit',
                    aiWritable: true,
                    maxLength: 60,
                },
                {
                    id: 'text',
                    type: 'textarea',
                    label: 'Text',
                    default: '',
                    aiWritable: true,
                    maxLength: 240,
                    info: 'Shown under the list, only while this feature is the one being read.',
                },
                {
                    id: 'image',
                    type: 'image',
                    label: 'Image',
                    info: 'One photo per feature. It is shown large while this feature is the one being read, and it is this feature’s thumbnail in the strip.',
                },
            ],
        },
    ],
    maxBlocks: 8,
};
export const featureGalleryPresets = [
    {
        name: 'Feature gallery',
        // `colorScheme: 'inverse'` — le fond sombre de la référence, sur lequel un titre net à côté de
        // titres en retrait se lit d'un coup d'œil. Une case à décocher si la boutique est claire.
        settings: {
            heading: '@t:section.feature_gallery_heading',
            mediaPosition: 'left',
            imageRatio: 'square',
            showThumbnails: true,
            colorScheme: 'inverse',
            width: 'standard',
            paddingTop: 72,
            paddingBottom: 72,
        },
        blocks: [
            {
                type: 'feature',
                settings: {
                    title: '@t:section.feature_gallery_title_1',
                    text: '@t:section.feature_gallery_text_1',
                },
            },
            {
                type: 'feature',
                settings: {
                    title: '@t:section.feature_gallery_title_2',
                    text: '@t:section.feature_gallery_text_2',
                },
            },
            {
                type: 'feature',
                settings: {
                    title: '@t:section.feature_gallery_title_3',
                    text: '@t:section.feature_gallery_text_3',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const featureGalleryMeta = {
    label: 'Feature gallery',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map